import { JOURNEY_EVENTS } from "../constants/journeyEvents";
import { captureCampaignAttribution } from "./campaignAttribution";

const {
  TOOL,
  initializeCookiebotConsentBridge,
  isToolAllowed,
  onConsentChange,
} = require("../../../../shared/cookiebotConsent");

const GTM_EVENT_NAMES = {
  [JOURNEY_EVENTS.loginAttempt]: "Login Attempt",
  [JOURNEY_EVENTS.loginSuccess]: "Login Success",
  [JOURNEY_EVENTS.loginFailure]: "Login Failure",
  [JOURNEY_EVENTS.clickPay]: "Click Pay",
  [JOURNEY_EVENTS.createPlan]: "Click Create Plan",
  [JOURNEY_EVENTS.paymentCompleted]: "Payment Completed",
  [JOURNEY_EVENTS.chatOpened]: "User opened chat widget",
};

export const getGtmRouteKey = () => {
  if (typeof window === "undefined") return undefined;
  const pathname = window.location?.pathname || "";
  if (!pathname || pathname === "/") return "home";
  return pathname
    .split("/")
    .filter(Boolean)
    .map((part) => (part.match(/^[a-z0-9-]+$/i) ? part : "dynamic"))
    .join("/");
};

const getStatusGroup = (value) => {
  const status = Number(value);
  if (!Number.isFinite(status) || status <= 0) return undefined;
  return `${Math.floor(status / 100)}xx`;
};

const getAccountCountBucket = (value) => {
  const count = Number(value);
  if (!Number.isFinite(count) || count <= 0) return undefined;
  if (count === 1) return "1";
  if (count <= 3) return "2-3";
  return "4+";
};

const removeEmptyValues = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) =>
        value !== undefined &&
        value !== null &&
        value !== "" &&
        (typeof value !== "number" || Number.isFinite(value)),
    ),
  );

let gtmLoaded = false;
let gtmConsentListenerInitialized = false;

const getGtmContainerId = () => {
  const raw = window.__ENV?.GTM_CONTAINER_ID || process.env.GTM_CONTAINER_ID;
  if (raw == null || typeof raw !== "string") return "";
  const id = raw.trim();
  return /^GTM-[A-Z0-9]+$/i.test(id) ? id : "";
};

const loadGtm = () => {
  const containerId = getGtmContainerId();
  if (!containerId || gtmLoaded || document.getElementById("soc-gtm-loader"))
    return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ "gtm.start": new Date().getTime(), event: "gtm.js" });

  const script = document.createElement("script");
  script.id = "soc-gtm-loader";
  script.async = true;
  script.setAttribute("data-cfasync", "false");
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
  gtmLoaded = true;
};

const loadGtmWhenAllowed = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  initializeCookiebotConsentBridge();
  if (!isToolAllowed(TOOL.GTM)) return;

  loadGtm();
};

export const initializeGtmDataLayer = () => {
  if (typeof window === "undefined" || typeof document === "undefined") return;

  loadGtmWhenAllowed();
  if (gtmConsentListenerInitialized) return;

  gtmConsentListenerInitialized = true;
  onConsentChange(loadGtmWhenAllowed);
};

export const trackGtmJourneyEvent = (journeyEvent, properties = {}) => {
  const event = GTM_EVENT_NAMES[journeyEvent];
  if (typeof window === "undefined" || !event) return;
  if (!isToolAllowed(TOOL.GTM)) return;

  loadGtmWhenAllowed();
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(
    removeEmptyValues({
      event,
      app: "payment_portal",
      route_key: properties.route_key || getGtmRouteKey(),
      flow: properties.flow,
      plan_type: properties.plan_type || properties.type,
      payment_method_type: properties.payment_method_type,
      payment_count_bucket: properties.payment_count_bucket,
      reason_category: properties.reason_category || properties.Reason,
      status_group: getStatusGroup(properties.status || properties.Status),
      account_count_bucket: getAccountCountBucket(properties.AccountCount),
    }),
  );
};

export const trackGa4Event = (event, properties = {}) => {
  if (typeof window === "undefined" || !event) return;
  if (!isToolAllowed(TOOL.GTM)) return;

  loadGtmWhenAllowed();
  window.dataLayer = window.dataLayer || [];
  const attribution = captureCampaignAttribution() || {};
  window.dataLayer.push(
    removeEmptyValues({
      ...properties,
      ...attribution,
      event,
      app: "payment_portal",
      route_key: properties.route_key || getGtmRouteKey(),
    }),
  );
};
